import { useMutation, useQuery } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import type { ILegalClientGroup } from '../../Interfaces/ILegalClientGroup';
import type { ILegalClientHasLegalClientGroup } from '../../Interfaces/ILegalClientHasLegalClientGroup';
import type { ILegalClient } from '../../Interfaces/ILegalClient';

///////////////////////////////
// GET: Alle LegalClientGroups
///////////////////////////////
export const useGetLegalClientGroups = () => {
  const {
    data: legalClientGroups = [],
    isLoading: isLoadingLegalClientGroups,
    refetch: refetchLegalClientGroups,
  } = useQuery<ILegalClientGroup[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalClientGroups'],
    queryFn: () => customFetcher({ endpoint: '/legalclientgroup' }),
  });

  return {
    legalClientGroups,
    isLoadingLegalClientGroups,
    refetchLegalClientGroups,
  };
};


///////////////////////////////
// CREATE: LegalClientGroup
///////////////////////////////
export const useCreateLegalClientGroup = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (input: { Name: string; ColorHex: string; Description: string | null }) =>
      customFetcher({
        endpoint: '/legalclientgroup',
        method: 'POST',
        body: input,
      }),
  });

  return {
    createLegalClientGroup: mutateAsync,
    isPendingCreateLegalClientGroup: isPending,
  };
};


///////////////////////////////
// UPDATE: LegalClientGroup
///////////////////////////////
export const useUpdateLegalClientGroup = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (group: ILegalClientGroup) =>
      customFetcher({
        endpoint: '/legalclientgroup',
        method: 'PUT',
        body: group,
      }),
  });

  return {
    updateLegalClientGroup: mutateAsync,
    isPendingUpdateLegalClientGroup: isPending,
  };
};

///////////////////////////////
// DELETE: LegalClientGroup
///////////////////////////////
export const useDeleteLegalClientGroup = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (idLegalClientGroup: number) =>
      customFetcher({
        endpoint: `/legalclientgroup/${idLegalClientGroup}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalClientGroup: mutateAsync,
    isPendingDeleteLegalClientGroup: isPending,
  };
};

///////////////////////////////
// GET: Join-Tabelle
// legalclient_has_legalclientgroup
///////////////////////////////
export const useGetLegalClientGroupJoins = () => {
  const {
    data: groupJoin = [],
    isLoading: isLoadingGroupJoin,
    refetch: refetchGroupJoin,
  } = useQuery<ILegalClientHasLegalClientGroup[]>({
    queryKey: ['legalClientGroupJoin'],
    queryFn: () =>
      customFetcher({
        endpoint: '/legalclient_has_legalclientgroup',
      }),
  });

  return {
    groupJoin,
    isLoadingGroupJoin,
    refetchGroupJoin,
  };
};

///////////////////////////////
// GET: Members einer Gruppe
// /legalclientgroup/{id}/members
///////////////////////////////
export const useGetLegalClientGroupMembers = (idLegalClientGroup: number | null) => {
  const {
    data: members = [],
    isLoading,
    isRefetching,
    refetch,
  } = useQuery<ILegalClient[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalClientGroupMembers', idLegalClientGroup],
    enabled: typeof idLegalClientGroup === 'number' && idLegalClientGroup > 0,
    queryFn: () =>
      customFetcher({
        endpoint: `/legalclientgroup/${idLegalClientGroup}/members`,
      }),
  });

  return {
    members,
    isLoadingMembers: isLoading,
    isRefetchingMembers: isRefetching,
    refetchMembers: refetch,
  };
};


export const useSetLegalClientGroupMembers = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (input: { idLegalClientGroup: number; ids: number[] }) =>
      customFetcher({
        endpoint: `/legalclientgroup/${input.idLegalClientGroup}/members`,
        method: 'PUT',
        body: { ids: input.ids },
      }),
  });

  return { setMembers: mutateAsync, isPendingSetMembers: isPending };
};



